// App settings (§13.7): alert toggles, change MPIN / change password and
// de-register (MPIN-confirmed).
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faChevronRight,
  faKey,
  faLock,
  faUserXmark,
} from '@fortawesome/free-solid-svg-icons';
import { deregister, getSettings, saveSettings } from '@/api/profile';
import { ApiError } from '@/api/client';
import { MOCK_PROFILE } from '@/api/flags';
import type { ProfileSettings } from '@/types/phase2.types';
import Loader from '@/components/ui/Loader/Loader';
import Button from '@/components/ui/Button/Button';
import PinPad from '@/components/common/auth/PinPad';
import './profile.css';

const TOGGLES: Array<{ key: keyof ProfileSettings; title: string; sub: string }> = [
  { key: 'smsServices', title: 'SMS services', sub: 'Transaction alerts by SMS' },
  { key: 'notifications', title: 'Notifications', sub: 'Payment and account notifications' },
  { key: 'soundNotification', title: 'Sound for notifications', sub: 'Play a sound with each notification' },
];

const Settings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<ProfileSettings | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deregisterOpen, setDeregisterOpen] = useState(false);
  const [pinError, setPinError] = useState('');
  const [pinResetKey, setPinResetKey] = useState(0);

  useEffect(() => {
    getSettings()
      .then(setSettings)
      .catch(() => setSettings(null));
  }, []);

  const toggle = async (key: keyof ProfileSettings) => {
    if (!settings) return;
    const next = { ...settings, [key]: !settings[key] };
    setSettings(next);
    try {
      await saveSettings(next);
    } catch {
      setSettings(settings);
      toast.error('Could not save the setting. Try again.');
    }
  };

  const handleDeregisterPin = async (mpin: string) => {
    setPinError('');
    try {
      await deregister(mpin);
      setDeregisterOpen(false);
      toast.success('Mobile banking de-registered');
      navigate('/', { replace: true });
    } catch (err) {
      setPinError(err instanceof ApiError ? err.message : 'Could not de-register. Try again.');
      setPinResetKey((k) => k + 1);
    }
  };

  // The demo keeps its own change-MPIN screen; the real API sets the MPIN on
  // the MPIN page and comes back here.
  const mpinItem = MOCK_PROFILE
    ? {
        icon: faKey,
        title: 'Change MPIN',
        sub: 'A new six-digit MPIN, confirmed by OTP',
        to: '/profile/settings/change-mpin',
      }
    : {
        icon: faKey,
        title: 'Set or change MPIN',
        sub: 'A new four-digit MPIN, confirmed by OTP',
        to: '/settings/mpin?return=/profile/settings',
      };

  const menu = [
    mpinItem,
    { icon: faLock, title: 'Change password', sub: 'A new login password, confirmed by OTP', to: '/profile/settings/change-password' },
  ];

  return (
    <div className="nova-profile-page">
      <div className="nova-pay-flow-header">
        <button type="button" className="nova-icon-btn" title="Back" aria-label="Back" onClick={() => navigate('/profile')}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>App settings</h2>
      </div>

      {!settings && <Loader rows={3} />}
      {settings && (
        <>
          {TOGGLES.map((t) => (
            <div key={t.key} className="nova-setting-row">
              <span className="nova-setting-text">
                <strong>{t.title}</strong>
                <span>{t.sub}</span>
              </span>
              <div className="form-check form-switch m-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  checked={settings[t.key]}
                  onChange={() => void toggle(t.key)}
                  aria-label={t.title}
                />
              </div>
            </div>
          ))}

          <div className="nova-profile-menu mt-3">
            {menu.map((item) => (
              <button
                type="button"
                key={item.to}
                className="nova-profile-menu-row"
                onClick={() => navigate(item.to)}
              >
                <span className="nova-profile-menu-icon">
                  <FontAwesomeIcon icon={item.icon} />
                </span>
                <span className="nova-ben-row-text">
                  <span className="nova-ben-alias">{item.title}</span>
                  <span className="nova-ben-count">{item.sub}</span>
                </span>
                <FontAwesomeIcon icon={faChevronRight} className="nova-pay-chevron" />
              </button>
            ))}

            <button type="button" className="nova-profile-menu-row nova-profile-danger" onClick={() => setConfirmOpen(true)}>
              <span className="nova-profile-menu-icon">
                <FontAwesomeIcon icon={faUserXmark} />
              </span>
              <span className="nova-ben-row-text">
                <span className="nova-ben-alias">De-register</span>
                <span className="nova-ben-count">Turn mobile banking off for this account</span>
              </span>
              <FontAwesomeIcon icon={faChevronRight} className="nova-pay-chevron" />
            </button>
          </div>
        </>
      )}

      <Modal show={confirmOpen} onHide={() => setConfirmOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>De-register?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          De-registering turns mobile banking off for this account. You can set it up again later from the branch or
          by registering again.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline" onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmOpen(false);
              setPinError('');
              setDeregisterOpen(true);
            }}
          >
            Continue
          </Button>
        </Modal.Footer>
      </Modal>

      <PinPad
        show={deregisterOpen}
        variant="MPIN"
        title="Enter your MPIN to de-register"
        error={pinError}
        resetKey={pinResetKey}
        onComplete={(pin) => void handleDeregisterPin(pin)}
        onClose={() => setDeregisterOpen(false)}
      />
    </div>
  );
};

export default Settings;
